import { useMemo, useEffect, useState } from 'react';
import { instance } from '../api/api';
import { formatDateForApi } from '../utils/date.utils';

interface FilterParams {
  fullName?: string;
  login?: string;
  phone?: string;
  role?: string;
  isActive?: boolean | string;
  page?: number;
  limit?: number;
  sort?: string;
  query?: string;
  filterQuery?: string;
  startTime?: string;
  endTime?: string;
  [key: string]: string | number | boolean | undefined;
}

interface BackendResponse {
  data: Array<Record<string, any>>;
  page: number;
  totalCount: number;
  totalPages: number;
}

export const useBackendFilter = (apiEndpoint: string = '/profiles') => {
  const [data, setData] = useState<Array<Record<string, any>>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = async (params: FilterParams = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Формируем параметры для API запроса
      const apiParams: Record<string, string | number | boolean> = {};

      // Обработка специального случая с query и filterQuery (используется в Admin.tsx)
      if (params.query && params.filterQuery) {
        // Используем filterQuery как имя поля для поиска
        apiParams[params.filterQuery as string] = params.query;
      }

      // Основные параметры поиска, если они указаны напрямую
      if (params.fullName) {
        apiParams.fullName = params.fullName;
      }

      if (params.login) {
        apiParams.login = params.login;
      }

      if (params.phone) {
        apiParams.phone = params.phone;
      }

      if (params.role) {
        apiParams.role = params.role;
      }

      if (params.isActive !== undefined) {
        apiParams.isActive = params.isActive;
      }

      // Параметры пагинации
      apiParams.page = params.page || 1;
      apiParams.limit = params.limit || 10;

      // Добавляем сортировку
      if (params.sort) {
        apiParams.sort = params.sort;
      }

      // Форматируем даты, если они есть
      if (params.startTime) {
        apiParams.startTime = formatDateForApi(params.startTime) as string;
      }

      if (params.endTime) {
        apiParams.endTime = formatDateForApi(params.endTime) as string;
      }

      // Копируем остальные параметры, пропуская уже обработанные
      Object.keys(params).forEach((key) => {
        if (key !== 'query' && key !== 'filterQuery' && !apiParams[key] && params[key] !== undefined) {
          apiParams[key] = params[key] as string | number | boolean;
        }
      });

      const response = await instance.get<BackendResponse>(apiEndpoint, { params: apiParams });

      if (response.data?.data) {
        setData(response.data.data);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
        setCurrentPage(response.data.page);
      } else {
        // Обрабатываем случай, когда сервер вернул массив напрямую
        const responseData = Array.isArray(response.data)
          ? response.data
          : typeof response.data === 'object' && response.data !== null
            ? [response.data]
            : [];

        setData(responseData);
        setTotalCount(responseData.length);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (err) {
      setError('Ошибка при получении данных с сервера');
      console.error('Ошибка при загрузке данных:', err);
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    totalCount,
    totalPages,
    currentPage,
    fetchData,
  };
};
